import {
  resolveCueFireAtMs,
  type TimerSoundCue,
  type TimerState,
} from './timerState';

export function buildCueKey(cue: TimerSoundCue): string {
  return `${cue.anchor}:${cue.atSeconds}:${cue.audioId}`;
}

/**
 * Milliseconds until a cue should fire, or null if it cannot fire for this timer state.
 * Returns 0 when the cue is already due.
 */
export function computeCueDelayMs(
  state: TimerState,
  cue: TimerSoundCue,
  nowMs: number
): number | null {
  const fireAt = resolveCueFireAtMs(state, cue);
  if (fireAt === null) return null;
  if (state.stoppedAt !== null && state.stoppedAt < fireAt) return null;
  return Math.max(0, fireAt - nowMs);
}

export interface ScheduledCueTimeouts {
  timeoutIds: ReturnType<typeof setTimeout>[];
  clear: () => void;
}

/**
 * Schedule callbacks for timer sound cues that have not yet fired.
 */
export function scheduleTimerSoundCues(
  state: TimerState,
  cues: TimerSoundCue[],
  firedKeys: Set<string>,
  onCue: (cue: TimerSoundCue) => void,
  nowMs: number = Date.now()
): ScheduledCueTimeouts {
  const timeoutIds: ReturnType<typeof setTimeout>[] = [];

  for (const cue of cues) {
    const key = buildCueKey(cue);
    if (firedKeys.has(key)) continue;

    const delay = computeCueDelayMs(state, cue, nowMs);
    if (delay === null) continue;

    const id = setTimeout(() => {
      if (firedKeys.has(key)) return;
      firedKeys.add(key);
      onCue(cue);
    }, delay);
    timeoutIds.push(id);
  }

  return {
    timeoutIds,
    clear: () => {
      for (const id of timeoutIds) clearTimeout(id);
    },
  };
}
